import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { cityCodeAPI } from '../../services/api';

const CityCodesPage = () => {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');

  // Add form
  const [addName, setAddName]   = useState('');
  const [addCode, setAddCode]   = useState('');
  const [adding, setAdding]     = useState(false);

  // Edit state
  const [editId, setEditId]     = useState(null);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [saving, setSaving]     = useState(false);

  const load = () => {
    setLoading(true);
    cityCodeAPI.getAll()
      .then((res) => setRows(res.data.data || []))
      .catch(() => toast.error('فشل تحميل رموز المدن'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = rows.filter(
    (r) => r.name.includes(search) || r.code.toUpperCase().includes(search.toUpperCase())
  );

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addName.trim() || !addCode.trim()) return;
    setAdding(true);
    try {
      await cityCodeAPI.create({ name: addName.trim(), code: addCode.trim() });
      toast.success(`تمت إضافة "${addName.trim()}"`);
      setAddName(''); setAddCode('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الإضافة');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditName(row.name);
    setEditCode(row.code);
  };

  const handleSave = async (id) => {
    setSaving(true);
    try {
      await cityCodeAPI.update(id, { name: editName.trim(), code: editCode.trim() });
      toast.success('تم الحفظ');
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`حذف "${row.name}" (${row.code})؟`)) return;
    try {
      await cityCodeAPI.remove(row.id);
      toast.success('تم الحذف');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الحذف');
    }
  };

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6 max-w-3xl mx-auto">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">🗺️ رموز المدن</h1>
        <p className="text-sm text-gray-500 mt-1">تُستخدم في أرقام التذاكر وملفات PDF — يراها السائق والمسافر.</p>
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="bg-white rounded-2xl shadow p-5 flex gap-3 items-end flex-wrap">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs text-gray-500 mb-1">اسم المدينة بالعربية</label>
          <input
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            placeholder="مثال: القلابات"
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="w-28">
          <label className="block text-xs text-gray-500 mb-1">الرمز (2-3 حروف)</label>
          <input
            value={addCode}
            onChange={(e) => setAddCode(e.target.value.toUpperCase().slice(0, 3))}
            placeholder="QLB"
            maxLength={3}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          type="submit"
          disabled={adding || !addName.trim() || !addCode.trim()}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl px-5 py-2 text-sm font-semibold transition"
        >
          {adding ? '...' : '+ إضافة'}
        </button>
      </form>

      {/* Search + table */}
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
          <span className="font-semibold text-gray-700 text-sm">
            القائمة <span className="text-gray-400 font-normal">({filtered.length})</span>
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث..."
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">جاري التحميل...</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">لا توجد نتائج</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-5 py-3 text-right font-medium">اسم المدينة</th>
                <th className="px-5 py-3 text-right font-medium">الرمز</th>
                <th className="px-5 py-3 text-left font-medium">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50/60">
                  {editId === row.id ? (
                    <>
                      <td className="px-4 py-2">
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="border border-blue-300 rounded-lg px-2 py-1 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          value={editCode}
                          onChange={(e) => setEditCode(e.target.value.toUpperCase().slice(0, 3))}
                          maxLength={3}
                          className="border border-blue-300 rounded-lg px-2 py-1 text-sm w-20 font-mono uppercase focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-2 text-left">
                        <button
                          onClick={() => handleSave(row.id)}
                          disabled={saving}
                          className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg ml-1 transition"
                        >
                          {saving ? '...' : 'حفظ'}
                        </button>
                        <button
                          onClick={() => setEditId(null)}
                          className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 transition"
                        >
                          إلغاء
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-5 py-3 font-medium text-gray-800">{row.name}</td>
                      <td className="px-5 py-3 font-mono text-blue-700 font-semibold">{row.code}</td>
                      <td className="px-5 py-3 text-left">
                        <button
                          onClick={() => startEdit(row)}
                          className="text-xs text-blue-600 hover:text-blue-800 ml-3 transition"
                        >
                          تعديل
                        </button>
                        <button
                          onClick={() => handleDelete(row)}
                          className="text-xs text-red-500 hover:text-red-700 transition"
                        >
                          حذف
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CityCodesPage;
